import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { HttpErrorResponse } from '@angular/common/http';

import { ModuleConfiguration } from '../dynamicDataSet.module.config';

import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import { DynamicDataSetService } from './DynamicDataSetService';
import { DynamicDataSetListModel } from '../models/DynamicDataSetListModel';
import { QueryResponse } from '../../core/services/QueryResponse';
import { DynamicDataSetModel } from '../models/DynamicDataSetModel';
import { ModelResponse } from '../../core/services/ModelResponse';
import { ResponseMessage } from '../../core/services/ResponseMessage';
import { ResponseMessageType } from '../../core/services/ResponseMessageType';
import { LoadPagePayload } from '../../pillar/common/actions/LoadPagePayload';
import { PhysicalAddressModel } from '../../common/models/PhysicalAddressModel';
import { ActionError } from '../../pillar/common/actions/ActionError';
import { AddDynamicDataSetModel } from '../models/AddDynamicDataSetModel';
import { DynamicEntityModel } from '../models/DynamicEntityModel';
import { PropertyModel } from '../models/PropertyModel';
import { DataLinkModel } from '../models/DataLinkModel';
import { FINALIZE_DYNAMICDATASET, FinalizeDynamicDataSetAction } from '../store/actions/FinalizeDynamicDataSetAction';
import { FinalizeDynamicDataSetSuccessAction } from '../store/actions/FinalizeDynamicDataSetSuccessAction';
import { DynamicRuleProfileModel } from '../models/DynamicRuleProfileModel';
import { CREATEDRAFT_DYNAMICDATASET, CreateDraftDynamicDataSetAction } from '../store/actions/CreateDraftDynamicAction';
import { CreateDraftDynamicDataSetSuccessAction } from '../store/actions/CreateDraftDynamicSuccessAction';
import { DynamicRuleModel } from '../models/DynamicRuleModel';
import { RuleExpressionModel } from '../models/RuleExpressionModel';
import { LogicalExpressionModel } from '../models/LogicalExpressionModel';
import { GROUP_RULE_CONDITIONS, GroupRuleConditionsAction } from '../store/actions/GroupRuleConditionsAction';
import { GroupRuleConditionsSuccessAction } from '../store/actions/GroupRuleConditionsSuccessAction';
import { UNGROUP_RULE_CONDITIONS, UngroupRuleConditionsAction } from '../store/actions/UngroupRuleConditionsAction';
import { UngroupRuleConditionsSuccessAction } from '../store/actions/UngroupRuleConditionsSuccessAction';
import { UNFINALIZE_DYNAMICDATASET, UnfinalizeDynamicDataSetAction } from '../store/actions/UnfinalizeDynamicDataSet';
import { UnfinalizeDynamicDataSetSuccessAction } from '../store/actions/UnfinalizeDynamicDataSetSuccessAction';

@Injectable()
export class DynamicDataSetServiceEffects {
  @Effect()
  getDynamicDataSetList$: Observable<Action> = this.actions$.ofType(dashboardActions.getQueryListType<DynamicDataSetListModel>(ModuleConfiguration.moduleName, DynamicDataSetListModel))
    .map((a: dashboardActions.QueryListAction<DynamicDataSetListModel>) => a.payload)
      .switchMap((payload: LoadPagePayload) => this.DynamicDataSetService.getDynamicDataSetList(payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.searchTerm)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicDataSetListModel>(error)))
      .map((response: QueryResponse<DynamicDataSetListModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, DynamicDataSetListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, DynamicDataSetListModel, { items: response.items, totalCount: response.totalCount });
      }));
  @Effect()
  getDynamicDataSet$: Observable<Action> = this.actions$.ofType(dashboardActions.getSelectionType<DynamicDataSetModel>(ModuleConfiguration.moduleName, DynamicDataSetModel))
      .switchMap((a: dashboardActions.GetSelectionAction<DynamicDataSetModel>) => this.DynamicDataSetService.getDynamicDataSet(a.payload.key)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicDataSetModel>(error)))
      .map((response: ModelResponse<DynamicDataSetModel>) => {
        if (response.hasError) {
          return new ActionError( a, response.messages.map(m => m.value) );
        }
        return new dashboardActions.GetSelectionSuccessAction(ModuleConfiguration.moduleName, DynamicDataSetModel, { model: response.model });
      }));
  @Effect()
  getDynamicRule$: Observable<Action> = this.actions$.ofType(dashboardActions.getSelectionType<DynamicRuleModel>(ModuleConfiguration.moduleName, DynamicRuleModel))
      .switchMap((a: dashboardActions.GetSelectionAction<DynamicRuleModel>) => this.DynamicDataSetService.getDynamicRule(a.payload.key, a.payload.subKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicRuleModel>(error)))
      .map((response: ModelResponse<DynamicRuleModel>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new dashboardActions.GetSelectionSuccessAction(ModuleConfiguration.moduleName, DynamicRuleModel, { model: response.model });
      }));
  @Effect()
  addDynamicDataSet$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<AddDynamicDataSetModel>(ModuleConfiguration.moduleName, AddDynamicDataSetModel))
      .switchMap((a: dashboardActions.AddAction<string>) => this.DynamicDataSetService.addDynamicDataSet(a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<AddDynamicDataSetModel>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, AddDynamicDataSetModel, { key: response.model });
      }));
  @Effect()
  reviseEntity$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicDataSetModel, DynamicEntityModel>(ModuleConfiguration.moduleName, DynamicDataSetModel, DynamicEntityModel))
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicDataSetModel, DynamicEntityModel>) => this.DynamicDataSetService.reviseEntity(a.payload.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicEntityModel>(error)))
        .map((response: ModelResponse<DynamicEntityModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicDataSetModel,
          DynamicEntityModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  addProperty$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicEntityModel, PropertyModel>(ModuleConfiguration.moduleName, DynamicEntityModel, PropertyModel))
    .filter((a: workspaceActions.UpdateModelAction<DynamicEntityModel, PropertyModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
    .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, PropertyModel>) => this.DynamicDataSetService.addProperty(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<PropertyModel>(error)))
      .map((response: ModelResponse<PropertyModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          PropertyModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  addDataLink$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicEntityModel, DataLinkModel>(ModuleConfiguration.moduleName, DynamicEntityModel, DataLinkModel))
      .filter((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DataLinkModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DataLinkModel>) => this.DynamicDataSetService.addDataLink(a.payload.key, a.payload.model.propertyKey, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DataLinkModel>(error)))
        .map((response: ModelResponse<DataLinkModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          DataLinkModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  addRule$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicEntityModel, DynamicRuleProfileModel>(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel))
      .filter((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DynamicRuleProfileModel>) => a.payload.model.key === undefined || a.payload.model.key === '')
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DynamicRuleProfileModel>) => this.DynamicDataSetService.addRule(a.payload.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicRuleProfileModel>(error)))
        .map((response: ModelResponse<DynamicRuleProfileModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          DynamicRuleProfileModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseProperty$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicEntityModel, PropertyModel>(ModuleConfiguration.moduleName, DynamicEntityModel, PropertyModel))
      .filter((a: workspaceActions.UpdateModelAction<DynamicEntityModel, PropertyModel>) => a.payload.model.key !== undefined && a.payload.model.key !== '')
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, PropertyModel>) => this.DynamicDataSetService.reviseProperty(a.payload.key, a.payload.model.key, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<PropertyModel>(error)))
      .map((response: ModelResponse<PropertyModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          PropertyModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseDataLink$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicEntityModel, DataLinkModel>(ModuleConfiguration.moduleName, DynamicEntityModel, DataLinkModel))
    .filter((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DataLinkModel>) => a.payload.model.key !== undefined && a.payload.model.key !== '')
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DataLinkModel>) => this.DynamicDataSetService.reviseDataLink(a.payload.key, a.payload.model.propertyKey, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DataLinkModel>(error)))
      .map((response: ModelResponse<DataLinkModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          DataLinkModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseRule$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicEntityModel, DynamicRuleProfileModel>(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel))
      .filter((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DynamicRuleProfileModel>) => a.payload.model.key !== undefined && a.payload.model.key !== '')
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DynamicRuleProfileModel>) => this.DynamicDataSetService.reviseRule(a.payload.key, a.payload.model.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicRuleProfileModel>(error)))
        .map((response: ModelResponse<DynamicRuleProfileModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          DynamicRuleProfileModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  deleteProperty$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<DynamicEntityModel, PropertyModel>(ModuleConfiguration.moduleName, DynamicEntityModel, PropertyModel))
    .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, PropertyModel>) => this.DynamicDataSetService.deleteProperty(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<PropertyModel>(error)))
      .map((response: ModelResponse<PropertyModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          PropertyModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  deleteDataLink$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<DynamicEntityModel, DataLinkModel>(ModuleConfiguration.moduleName, DynamicEntityModel, DataLinkModel))
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DataLinkModel>) => this.DynamicDataSetService.deleteDataLink(a.payload.key, a.payload.model.propertyKey, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DataLinkModel>(error)))
      .map((response: ModelResponse<DataLinkModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          DataLinkModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  deleteRule$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<DynamicEntityModel, DynamicRuleProfileModel>(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel))
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicEntityModel, DynamicRuleProfileModel>) => this.DynamicDataSetService.deleteRule(a.payload.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicRuleProfileModel>(error)))
        .map((response: ModelResponse<DynamicRuleProfileModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicEntityModel,
          DynamicRuleProfileModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  finalizeDynamicDataSet$: Observable<Action> = this.actions$.ofType(FINALIZE_DYNAMICDATASET)
      .switchMap((a: FinalizeDynamicDataSetAction) => this.DynamicDataSetService.finalizeDynamicDataSet(a.payload.dynamicDataSetKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicDataSetModel>(error)))
      .map((response: ModelResponse<DynamicDataSetModel>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new FinalizeDynamicDataSetSuccessAction({ dynamicDataSet: response.model });
      }));
  @Effect()
  unfinalizeDynamicDataSet$: Observable<Action> = this.actions$.ofType(UNFINALIZE_DYNAMICDATASET)
      .switchMap((a: UnfinalizeDynamicDataSetAction) => this.DynamicDataSetService.unfinalizeDynamicDataSet(a.payload.dynamicDataSetKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicDataSetModel>(error)))
      .map((response: ModelResponse<DynamicDataSetModel>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new UnfinalizeDynamicDataSetSuccessAction({ dynamicDataSet: response.model });
      }));
  @Effect()
  createDraftDynamicDataSet$: Observable<Action> = this.actions$.ofType(CREATEDRAFT_DYNAMICDATASET)
    .switchMap((a: CreateDraftDynamicDataSetAction) => this.DynamicDataSetService.createDraftDynamicDataSet(a.payload.dynamicDataSetKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DynamicDataSetModel>(error)))
      .map((response: ModelResponse<DynamicDataSetModel>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new CreateDraftDynamicDataSetSuccessAction({ dynamicDataSet: response.model });
      }));
  @Effect()
  reviseRuleExpression$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicRuleModel, RuleExpressionModel>(ModuleConfiguration.moduleName, DynamicRuleModel, RuleExpressionModel))
     .switchMap((a: workspaceActions.UpdateModelAction<DynamicRuleModel, RuleExpressionModel>) => this.DynamicDataSetService.reviseRuleExpression(a.payload.key, a.payload.model.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<RuleExpressionModel>(error)))
        .map((response: ModelResponse<RuleExpressionModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicRuleModel,
          RuleExpressionModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  addRuleCondition$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicRuleModel, LogicalExpressionModel>(ModuleConfiguration.moduleName, DynamicRuleModel, LogicalExpressionModel))
      .filter((a: workspaceActions.UpdateModelAction<DynamicRuleModel, LogicalExpressionModel>) => a.payload.model.hashCode === undefined || a.payload.model.hashCode === '')
      .switchMap((a: workspaceActions.UpdateModelAction<DynamicRuleModel, LogicalExpressionModel>) => this.DynamicDataSetService.addRuleCondition(a.payload.key, a.payload.model.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LogicalExpressionModel>(error)))
        .map((response: ModelResponse<LogicalExpressionModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicRuleModel,
          LogicalExpressionModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  reviseRuleCondition$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<DynamicRuleModel, LogicalExpressionModel>(ModuleConfiguration.moduleName, DynamicRuleModel, LogicalExpressionModel))
    .filter((a: workspaceActions.UpdateModelAction<DynamicRuleModel, LogicalExpressionModel>) => a.payload.model.hashCode !== undefined && a.payload.model.hashCode !== '')
    .switchMap((a: workspaceActions.UpdateModelAction<DynamicRuleModel, LogicalExpressionModel>) => this.DynamicDataSetService.reviseRuleCondition(a.payload.key, a.payload.model.key, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LogicalExpressionModel>(error)))
      .map((response: ModelResponse<LogicalExpressionModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicRuleModel,
          LogicalExpressionModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  deleteRuleCondition$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<DynamicRuleModel, LogicalExpressionModel>(ModuleConfiguration.moduleName, DynamicRuleModel, LogicalExpressionModel))
    .switchMap((a: workspaceActions.UpdateModelAction<DynamicRuleModel, LogicalExpressionModel>) => this.DynamicDataSetService.deleteRuleCondition(a.payload.key, a.payload.model.key, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LogicalExpressionModel>(error)))
      .map((response: ModelResponse<LogicalExpressionModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          DynamicRuleModel,
          LogicalExpressionModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  groupRuleCondition$: Observable<Action> = this.actions$.ofType(GROUP_RULE_CONDITIONS)
    .switchMap((a: GroupRuleConditionsAction) => this.DynamicDataSetService.groupRuleCondition(a.payload.dynamicDataSetKey, a.payload.dynamicRuleKey, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new GroupRuleConditionsSuccessAction({ dynamicDataSetKey: a.payload.dynamicDataSetKey, dynamicRuleKey: a.payload.dynamicRuleKey, groupName: response.model });
      }));
  @Effect()
  ungroupRuleCondition$: Observable<Action> = this.actions$.ofType(UNGROUP_RULE_CONDITIONS)
    .switchMap((a: UngroupRuleConditionsAction) => this.DynamicDataSetService.ungroupRuleCondition(a.payload.dynamicDataSetKey, a.payload.dynamicRuleKey, a.payload.groupName)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new UngroupRuleConditionsSuccessAction({ dynamicDataSetKey: a.payload.dynamicDataSetKey, dynamicRuleKey: a.payload.dynamicRuleKey, groupName: response.model });
      }));
  @Effect()
  deleteDataSet$: Observable<Action> = this.actions$.ofType(dashboardActions.deleteType<DynamicDataSetListModel>(ModuleConfiguration.moduleName, DynamicDataSetListModel))
    .switchMap((a: dashboardActions.DeleteAction<DynamicDataSetListModel>) => this.DynamicDataSetService.deleteDynamicDataSet(a.payload.model.key)
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new dashboardActions.DeleteSuccessAction(ModuleConfiguration.moduleName, DynamicDataSetListModel, { key: response.model });
      }));

  constructor(
    private DynamicDataSetService: DynamicDataSetService,
    private actions$: Actions
  ) { }
}



// WEBPACK FOOTER //
// ./src/app/dynamicDataSets/service/DynamicDataSetService.effects.ts